import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, CheckCircle2, ChevronDown, Minus } from 'lucide-react';
import { FlowButton } from '../ui/flow-button';
import './SpotlightComparisonTable.css';

const sharedFeature = (feature, plans) => plans.length > 1 && plans.every(plan => Object.is(feature.values[plan.id], feature.values[plans[0].id]));

// Adapted from Ivan's supplied SpotlightComparisonTable for the existing React
// router, brand tokens and gold actions. Comparison never starts a purchase.
export function SpotlightComparisonTable({ plans, groups, defaultSelectedPlanId,
  selectedPlanId, onSelectedPlanChange, defaultDifferencesOnly = false,
  differencesOnly, onDifferencesOnlyChange, onChoosePlan, labels = {}, className = '',
}) {
  const root = useRef(null);
  const stage = useRef(null);
  const [localPlan, setLocalPlan] = useState(defaultSelectedPlanId ?? plans[0]?.id ?? '');
  const [localDifferences, setLocalDifferences] = useState(defaultDifferencesOnly);
  const [reduced, setReduced] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const visibleGroups = useMemo(() => groups.map(group => ({ ...group, features: group.features.filter(feature => expanded || !feature.detail) })).filter(group => group.features.length), [groups, expanded]);
  const captionId = useId();
  const tableId = useId();
  const requestedId = selectedPlanId ?? localPlan;
  const activeId = plans.some(plan => plan.id === requestedId) ? requestedId : plans[0]?.id;
  const activeIndex = Math.max(0, plans.findIndex(plan => plan.id === activeId));
  const onlyDifferences = differencesOnly ?? localDifferences;
  const initialDifferences = useRef(onlyDifferences).current;
  const previousDifferences = useRef(onlyDifferences);
  const featureCount = visibleGroups.reduce((count, group) => count + group.features.length, 0);
  const sharedCount = visibleGroups.reduce((count, group) => count + group.features.filter(feature => sharedFeature(feature, plans)).length, 0);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);

  // Preserve the supplied component's FLIP squeeze: shared rows leave in place,
  // surviving rows move up, and the surrounding stage animates to its new height.
  useEffect(() => {
    const viewport = stage.current;
    const table = viewport?.querySelector('table');
    if (!viewport || !table) return undefined;
    const rows = Array.from(table.querySelectorAll('tr[data-layout-row]'));
    const shared = rows.filter(row => row.dataset.shared === 'true');
    const changed = previousDifferences.current !== onlyDifferences;
    previousDifferences.current = onlyDifferences;
    let live = true;
    const animations = [];
    const finish = () => {
      rows.forEach(row => {
        row.hidden = onlyDifferences && row.dataset.shared === 'true';
        row.removeAttribute('style');
        Array.from(row.cells).forEach(cell => cell.style.removeProperty('width'));
      });
      viewport.style.removeProperty('height');
    };
    if (reduced || !changed || typeof viewport.animate !== 'function') {
      finish();
      return undefined;
    }
    queueMicrotask(() => {
      if (!live) return;
      const oldHeight = viewport.getBoundingClientRect().height;
      const tableTop = table.getBoundingClientRect().top;
      const before = rows.map(row => ({ row, box: row.getBoundingClientRect(), widths: Array.from(row.cells).map(cell => cell.getBoundingClientRect().width) }));
      if (onlyDifferences) {
        before.filter(({ row }) => shared.includes(row)).forEach(({ row, box, widths }) => {
          Array.from(row.cells).forEach((cell, index) => { cell.style.width = `${widths[index]}px`; });
          Object.assign(row.style, { position: 'absolute', top: `${box.top - tableTop}px`, left: '0', width: '100%', display: 'table', tableLayout: 'fixed' });
        });
      } else shared.forEach(row => { row.hidden = false; });
      const finalHeight = table.getBoundingClientRect().height;
      const finalTop = table.getBoundingClientRect().top;
      const timing = { duration: 380, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' };
      before.filter(({ row }) => !shared.includes(row)).forEach(({ row, box }) => {
        const delta = (box.top - tableTop) - (row.getBoundingClientRect().top - finalTop);
        if (Math.abs(delta) > 0.5) animations.push(row.animate([{ transform: `translateY(${delta}px)` }, { transform: 'translateY(0)' }], timing));
      });
      shared.forEach(row => {
        if (onlyDifferences) row.querySelectorAll('[data-row-content]').forEach(content => {
          animations.push(content.animate([{ height: `${content.getBoundingClientRect().height}px`, minHeight: '0px', paddingTop: '8px', paddingBottom: '8px', opacity: 1 }, { height: '0px', minHeight: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 }], timing));
        });
        else animations.push(row.animate([{ opacity: 0, transform: 'scaleY(0)' }, { opacity: 1, transform: 'scaleY(1)' }], timing));
      });
      viewport.style.height = `${finalHeight}px`;
      animations.push(viewport.animate([{ height: `${oldHeight}px` }, { height: `${finalHeight}px` }], timing));
      Promise.all(animations.map(animation => animation.finished.catch(() => undefined))).then(() => {
        if (live) { finish(); animations.forEach(animation => animation.cancel()); }
      });
    });
    return () => { live = false; animations.forEach(animation => animation.cancel()); finish(); };
  }, [onlyDifferences, reduced, visibleGroups, plans]);

  const select = id => {
    if (selectedPlanId === undefined) setLocalPlan(id);
    onSelectedPlanChange?.(id);
  };
  const toggle = () => {
    if (differencesOnly === undefined) setLocalDifferences(!onlyDifferences);
    onDifferencesOnlyChange?.(!onlyDifferences);
  };
  const cellProps = id => ({ 'data-cell-plan': id, 'data-active': id === activeId,
    onMouseEnter: () => { if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) select(id); },
  });

  return (
    <div ref={root} className={`pkg-comparison ${className}`} data-slot="spotlight-comparison-table"
      data-selected-plan={activeId} data-differences={onlyDifferences} data-autoplay="false"
      style={{ '--comparison-index': activeIndex, '--comparison-count': plans.length || 1 }}>
      <div className="pkg-comparison__toolbar">
        <p id={captionId}>{labels.caption || 'Compare paperwork packages'}</p>
        <button type="button" role="switch" aria-checked={onlyDifferences} aria-controls={tableId} onClick={toggle} className="pkg-comparison__filter">
          {labels.differences || 'Show differences only'}
          <span className="pkg-comparison__switch" aria-hidden="true"><span /></span>
        </button>
      </div>
      {plans.length ? <>
        <div ref={stage} className="pkg-comparison__stage">
          <div className="pkg-comparison__spotlight" aria-hidden="true" />
          <table id={tableId} aria-labelledby={captionId}>
            <colgroup><col className="pkg-comparison__label-column" />{plans.map(plan => <col key={plan.id} />)}</colgroup>
            <thead><tr>
              <th scope="col" className="pkg-comparison__essentials">
                <span>{labels.features || 'Package overview'}</span>
                <span className="pkg-comparison__count" aria-live="polite">{onlyDifferences ? featureCount - sharedCount : featureCount} of {featureCount} rows</span>
              </th>
              {plans.map(plan => <th scope="col" key={plan.id} data-plan-header={plan.id} {...cellProps(plan.id)}>
                <h3><button type="button" aria-pressed={plan.id === activeId} onClick={() => select(plan.id)} data-select-plan={plan.id}>{plan.name}</button></h3>
                <p className="pkg-comparison__price" data-plan-price={plan.id}>{plan.price}</p>
                <FlowButton to={plan.href} data-plan-action={plan.id} onClick={() => { select(plan.id); onChoosePlan?.(plan); }} className="pkg-comparison__action">
                  {plan.actionLabel}
                </FlowButton>
              </th>)}
            </tr></thead>
            {visibleGroups.map(group => {
              const hiddenCount = group.features.filter(feature => sharedFeature(feature, plans)).length;
              return <tbody key={group.id}>
                <tr data-layout-row className="pkg-comparison__group"><th scope="rowgroup" colSpan={plans.length + 1}>
                  {group.label}{onlyDifferences && hiddenCount > 0 && <span>{hiddenCount} shared {hiddenCount === 1 ? 'row' : 'rows'} hidden</span>}
                </th></tr>
                {group.features.map(feature => {
                  const shared = sharedFeature(feature, plans);
                  return <tr key={feature.id} data-layout-row data-feature-row={feature.id} data-shared={shared} hidden={initialDifferences && shared} aria-hidden={onlyDifferences && shared ? true : undefined}>
                    <th scope="row"><div data-row-content>{feature.label}</div></th>
                    {plans.map(plan => {
                      const value = feature.values[plan.id];
                      return <td key={plan.id} {...cellProps(plan.id)}><div data-row-content><span className="pkg-comparison__value" key={`${plan.id}-${activeId}`}>
                        {typeof value === 'boolean' ? <><span className="pkg-comparison__mark" aria-hidden="true">{value ? <Check size={13} /> : <Minus size={13} />}</span><span className="sr-only">{value ? 'Included' : 'Not included'}</span></> : value ?? '—'}
                      </span></div></td>;
                    })}
                  </tr>;
                })}
              </tbody>;
            })}
          </table>
        </div>
        {onlyDifferences && sharedCount === featureCount && <p role="status" className="pkg-comparison__empty">The packages share every listed feature.</p>}
        {groups.some(group => group.features.some(feature => feature.detail)) && <button type="button" className="pkg-comparison__expand" aria-expanded={expanded} aria-controls={tableId} onClick={() => setExpanded(value => !value)}>
          {expanded ? 'Collapse full comparison' : 'Expand full comparison'}<ChevronDown size={15} aria-hidden="true" />
        </button>}
      </> : <p className="pkg-comparison__empty">No packages are available.</p>}
    </div>
  );
}

export function PaperworkPackageCarousel({ plans }) {
  const track = useRef(null);
  const [active, setActive] = useState(0);
  const move = index => {
    const next = Math.max(0, Math.min(plans.length - 1, index));
    const element = track.current;
    const card = element?.children[next];
    if (!card) return;
    setActive(next);
    element.scrollTo({ left: card.offsetLeft - element.children[0].offsetLeft, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  };
  return <div className="pkg-carousel" role="region" aria-roledescription="carousel" aria-label="Paperwork packages">
    <div className="pkg-carousel__track" ref={track} tabIndex={0} onKeyDown={event => {
      if (event.target !== event.currentTarget || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault(); move(active + (event.key === 'ArrowRight' ? 1 : -1));
    }} onScroll={() => {
      const element = track.current;
      const width = element.children[1]?.offsetLeft - element.children[0]?.offsetLeft;
      if (width) setActive(Math.max(0, Math.min(plans.length - 1, Math.round(element.scrollLeft / width))));
    }}>
      {plans.map((plan, index) => <article key={plan.id} className="pkg-carousel__card home-spotlight" role="group" aria-roledescription="slide" aria-label={`${plan.name} package, ${index + 1} of ${plans.length}`}>
        <div className="pkg-carousel__heading"><div><p>Paperwork package</p><h3>{plan.name}</h3></div><p data-plan-price={plan.id} className="pkg-carousel__price">{plan.price}</p></div>
        <p className="pkg-carousel__description">{plan.description}</p>
        <p className="pkg-carousel__documents">{plan.documentCount} documents included</p>
        <ul>{plan.items.map(item => <li key={item}><CheckCircle2 size={15} aria-hidden="true" />{item}</li>)}</ul>
        <FlowButton to={plan.href} data-plan-action={plan.id} className="pkg-carousel__action">{plan.actionLabel}</FlowButton>
      </article>)}
    </div>
    <div className="pkg-carousel__controls">
      <button type="button" aria-label="Previous package" disabled={active === 0} onClick={() => move(active - 1)}><ArrowLeft size={17} /></button>
      <div>{plans.map((plan, index) => <button type="button" key={plan.id} aria-label={`Show ${plan.name} package`} aria-current={active === index ? 'true' : undefined} onClick={() => move(index)}><span /></button>)}</div>
      <button type="button" aria-label="Next package" disabled={active === plans.length - 1} onClick={() => move(active + 1)}><ArrowRight size={17} /></button>
    </div>
  </div>;
}
